import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'store';
import {
    onOpenCreateSimulationForm,
    onToggleSimulation,
    onChangeSimulation,
    onOpenAcceptAlert,
    onOpenEditAlert,
    onOpenDeleteAlert,
    onInitializeSimulation,
    onFetchSimulations
} from 'store/slices/simulation';

import { styled, useTheme, Theme, alpha } from '@mui/material/styles';
import {
    Button,
    Divider,
    IconButton as MuIIconButton,
    Menu,
    MenuItem,
    Stack,
    Switch,
    Typography,
    ListItemIcon,
    ListItemText,
    Box as MuiBox
} from '@mui/material';

import Loader from './Loader';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import MoreVert from '@mui/icons-material/MoreVert';
import ConfirmSimulationAlert from './dialogs/ConfirmSimulationAlert';
import AddSimulation from './dialogs/AddSimulation/Index';
import useAuth from 'hooks/useAuth';

import { IconArrowMerge, IconTrash, IconEdit } from '@tabler/icons';
import AcceptSimulationAlert from './dialogs/AcceptSimulationAlert';
import EditSimulation from './dialogs/EditSimulation';
import DeleteSimulationAlert from './dialogs/DeleteSimulationAlert';

// Styles
interface BoxStyleProps {
    theme: Theme;
    open: boolean;
    isInitialized?: boolean;
    isActive: boolean;
    numSimulations: number;
}

const Box = styled(MuiBox, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open, isInitialized, isActive, numSimulations }: BoxStyleProps) => ({
        position: 'absolute',
        zIndex: theme.zIndex.appBar + 1,
        opacity: 0,
        top: -100,
        minWidth: 'fit-content',
        ...(numSimulations > 0 && {
            width: isActive ? '300px' : '150px',
            left: isActive ? 'calc(100vw - 50% - 150px)' : 'calc(100vw - 50% - 75px)',
            background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
            padding: isActive ? '4px 8px 4px 4px' : '11px 16px 11px 8px',
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.light + 50 : theme.palette.primary.light,
            borderRadius: '8px',
            transition: 'opacity 0.2s ease, top 0.5s ease 0.7s, width 0.5s ease .5s, left 0.5s ease .5s',
            ...(isInitialized && { top: '10px', opacity: 1 }),
            ...(open && {
                background: theme.palette.mode === 'dark' ? alpha(theme.palette.dark.main, 0.2) : alpha(theme.palette.secondary.light, 0.5)
            }),
            ...(isActive && {
                '&:hover': {
                    background:
                        theme.palette.mode === 'dark' ? alpha(theme.palette.dark.main, 0.2) : alpha(theme.palette.secondary.light, 0.5)
                }
            })
        })
    })
);

const IconButton = styled(MuIIconButton)({
    padding: '4px'
});

// ==============================|| SIMULATION ||============================== //

interface SimulationProps {
    children: React.ReactElement;
}

const SimulationPanel: FC<SimulationProps> = (props) => {
    const { children } = props;

    const theme = useTheme();
    const { user } = useAuth();
    const dispatch = useDispatch();

    const { isInitialized, isActive, simulationId, simulations, isLoading } = useSelector((state) => state.simulation);

    // const [isLoading, setIsLoading] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const openMenu = Boolean(menuAnchorEl);

    const loaderMessage = useMemo(() => {
        if (!simulationId) return 'Caricamento dei dati più recenti...';
        const simulation = simulations.find((s) => s.id === simulationId)?.name;
        if (simulation) {
            return `Caricamento dei dati della simulazione ${simulation}...`;
        }
        return 'Caricamento...';
    }, [simulationId, simulations]);

    const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleToggleSimulation = () => {
        if (!user) return;

        let cSimulationId = null;
        if (isActive) cSimulationId = null;
        else if (!isActive && simulationId) cSimulationId = simulationId;
        else if (!isActive && !simulationId && simulations.length > 0) cSimulationId = simulations[0].id;

        dispatch(onToggleSimulation(user.tenantId, user.userName, !isActive, cSimulationId));
    };

    const handleAddSimulation = () => {
        setAnchorEl(null);
        dispatch(onOpenCreateSimulationForm(true));
    };

    const handleChangeSimulation = (simulationId?: string) => {
        if (!user || !simulationId) return;
        setAnchorEl(null);

        try {
            dispatch(onChangeSimulation(user.tenantId, user.userName, simulationId));
        } catch (error) {
            console.log(error);
        }
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
    };

    const handleAccept = () => {
        dispatch(onOpenAcceptAlert(true));
    };

    const handleEdit = () => {
        setMenuAnchorEl(null);
        dispatch(onOpenEditAlert(true));
    };

    const handleDelete = async () => {
        setMenuAnchorEl(null);
        dispatch(onOpenDeleteAlert(true));
    };

    useEffect(() => {
        if (simulationId && isActive) {
            dispatch(onInitializeSimulation(simulationId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [simulationId, isActive]);

    useEffect(() => {
        (async () => {
            if (user) {
                const res = await dispatch(onFetchSimulations(user.tenantId));
                if (res) {
                    if (user.simulazioneId && user.simulazioneIsValid) {
                        dispatch(onInitializeSimulation(user.simulazioneId));
                    }

                    if (user.simulazioneId && !user.simulazioneIsValid) {
                        console.log('//TODO: Errore nel recupero della simulazione');
                    }

                    if (!user.simulazioneId && user.simulazioni.length > 0) {
                        dispatch(onInitializeSimulation());
                    }
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    return (
        <div style={{ position: 'relative' }}>
            <Box theme={theme} open={open} isActive={isActive} isInitialized={isInitialized} numSimulations={simulations.length}>
                <Stack direction="row" alignItems="center" spacing={1} display="flex">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch size="small" checked={isActive} onClick={handleToggleSimulation} />
                        {!isActive && <Typography variant="h5">Modalità simulazione</Typography>}
                    </Stack>

                    {isActive && (
                        <>
                            <Stack direction="row" alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' }, flexGrow: 1 }}>
                                <Typography variant="h6">Simulazione:</Typography>
                                <Button
                                    id="demo-positioned-button"
                                    aria-controls="demo-positioned-menu"
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    onClick={handleClickListItem}
                                    sx={{ color: 'grey.500', fontWeight: 400 }}
                                    endIcon={<KeyboardArrowDownIcon />}
                                >
                                    {simulations.find((simulationItem) => simulationItem.id === simulationId)?.name}
                                </Button>
                                <Menu
                                    id="demo-positioned-menu"
                                    aria-labelledby="demo-positioned-button"
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right'
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right'
                                    }}
                                    sx={{
                                        '& .MuiMenuItem-root': { py: 1, px: 2 }
                                    }}
                                >
                                    {simulations.map((simulation) => (
                                        <MenuItem key={simulation.id} onClick={() => handleChangeSimulation(simulation.id)}>
                                            <ListItemText inset>{simulation.name}</ListItemText>
                                        </MenuItem>
                                    ))}
                                    <Divider />
                                    <MenuItem onClick={handleAddSimulation}>
                                        <ListItemIcon>
                                            <AddIcon fontSize="small" color="success" />
                                        </ListItemIcon>
                                        <ListItemText>Nuova simulazione</ListItemText>
                                    </MenuItem>
                                </Menu>
                                <Divider orientation="vertical" flexItem />
                            </Stack>
                            <Stack direction="row" alignItems="center">
                                <Button id="accept-button" onClick={handleAccept} startIcon={<IconArrowMerge stroke={1.5} size="1.3rem" />}>
                                    Accetta simulazione
                                </Button>
                                <IconButton
                                    size="large"
                                    aria-controls={open ? 'demo-positioned-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    onClick={handleOpenMenu}
                                >
                                    <MoreVert fontSize="small" sx={{ fontWeight: 500 }} />
                                </IconButton>
                                <Menu
                                    id="demo-positioned-menu"
                                    aria-labelledby="demo-positioned-button"
                                    anchorEl={menuAnchorEl}
                                    open={openMenu}
                                    onClose={handleCloseMenu}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left'
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left'
                                    }}
                                >
                                    <MenuItem onClick={handleEdit}>
                                        <ListItemIcon>
                                            <IconEdit stroke={1.5} size="1.3rem" />
                                        </ListItemIcon>
                                        <ListItemText>Modifica</ListItemText>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem color={theme.palette.error.main} onClick={handleDelete}>
                                        <ListItemIcon>
                                            <IconTrash color={theme.palette.error.main} stroke={1.5} size="1.3rem" />
                                        </ListItemIcon>
                                        <Typography variant="body1" color="error">
                                            Cancella
                                        </Typography>
                                    </MenuItem>
                                </Menu>
                            </Stack>
                        </>
                    )}
                </Stack>
            </Box>

            {children}

            {/* CONFIRM SIMULATION ALERT */}
            <ConfirmSimulationAlert />

            {/* ADD SIMULATION */}
            <AddSimulation />

            {/* ADD SIMULATION ALERT */}
            <EditSimulation />

            {/* DELETE SIMULATION ALERT */}
            <DeleteSimulationAlert />

            {/* ACCEPT SIMULATION ALERT */}
            <AcceptSimulationAlert />

            {isLoading && <Loader isLoading title={loaderMessage} />}
        </div>
    );
};

export default SimulationPanel;
